/**
 * The Transformation Readiness Diagnostic — eight questions on two axes.
 *
 * Kagan's tool, handed over 2026-08-25 as standalone HTML. Unlike the other two
 * it arrived ALREADY BILINGUAL, so the Turkish is his and Halil's rather than
 * owed — see `diagnostic.tr.ts`, which is this file's twin.
 *
 * Extracted from the source programmatically rather than retyped: eight
 * questions x four options x two languages, four archetypes with six paragraphs
 * each, and four case studies is about nine thousand words. Hand-copying that is
 * how a digit or a diacritic goes missing without anybody noticing.
 *
 * THE OPTIONS ARE IN A DELIBERATELY SHUFFLED ORDER, and that is load-bearing:
 * a reader who spots that the last option always flatters them stops answering
 * and starts positioning. Do not "tidy" them into ascending order.
 *
 * Scoring is per axis and never combined into one number. Four answers per axis
 * sum to 4-16, then `(raw - 4) / 12` gives 0-100, and the quadrant is the pair
 * split at 50. "High will, low skill" and "low will, high skill" average
 * identically and mean opposite things, which is the whole reason this tool
 * reports two figures.
 */

export const diagnostic = {
  ui: {
    ax1name: 'Will',
    ax2name: 'Skill',
    ax1q: 'Are we clear, committed and aligned on what we want to achieve, and do we truly want to transform?',
    ax2q: 'Do we have the operational engine, bandwidth and resources to actually execute and finish?',
    ax1tag: 'Axis 1 · Will',
    ax2tag: 'Axis 2 · Skill',
    ax1card: 'Axis 1: intent and commitment',
    ax2card: 'Axis 2: capacity to execute',
    ax1items: [
      'Clarity of vision: an unambiguous target state and success metrics',
      'Leadership commitment: conviction, courage and resource allocation',
      'Organisational alignment: intent that survives middle management',
      'Strategic focus: explicit agreement on what NOT to do',
    ],
    ax2items: [
      'Talent & competency: skills, bench depth and domain expertise',
      'Process & governance: decision rights, delivery discipline, PMO maturity',
      'Infrastructure & technology: platforms, data architecture, capital',
      'Adaptive culture: speed, psychological safety, resilience',
    ],
    /**
     * The line under the start button, the same promise the other tools make
     * there. `{n}` is substituted with the real question count at render, so it
     * cannot drift from the list below; the duration matches the "8 QUESTIONS ·
     * 3 MINUTES" kicker this tool carries on the Tools hub.
     */
    qcount: '{n} questions · about three minutes',
    back: 'Previous question',
    yourprofile: 'Your readiness profile',
    whatthis: 'What this looks like in practice',
    picture: 'The picture',
    internal: 'Inside the organisation',
    external: 'How the market sees you',
    odds: 'Where this usually ends',
    imperative: 'Consulting imperative',
    breakdown: 'Your eight dimensions',
    breakdownsub: 'The average score hides the bottleneck. This is where it actually is.',
    risks: 'Your two highest-probability failure modes',
    riskssub: 'Derived from your two weakest dimensions, mapped to the documented failure patterns in transformation research.',
    precedent: 'Precedent',
    precedentsub: 'Two organisations that stood roughly where you stand, and went in opposite directions.',
    retake: 'Retake the diagnostic',
  },
  questions: [
    {
      axis: 'N',
      dim: 'Will · Clarity of vision',
      short: 'Clarity of vision',
      text: 'If we stopped ten people across the company and asked what this transformation is trying to achieve, what would we hear?',
      options: [
        'A general direction such as “digital”, “AI” or “efficiency”, but no target state anyone could measure.',
        'One consistent answer, including the target state and the numbers that will prove we got there.',
        'Ten different answers, and several would name a project code instead of a business outcome.',
        'A clear destination, but success is defined by what we will build, not by what will change in the business.',
      ],
    },
    {
      axis: 'N',
      dim: 'Will · Leadership commitment',
      short: 'Leadership commitment',
      text: 'When transformation demands a hard trade-off, such as budget, a senior role or a sacred-cow product, what actually happens?',
      options: [
        'The CEO decides, usually correctly, but every decision costs a fight and takes months.',
        'It gets deferred. The transformation adapts to the constraint; the constraint never adapts.',
        'Trade-offs are made openly and fast, and named leaders are personally measured on the outcome.',
        'We fund it, but nobody\'s bonus, title or mandate is genuinely at risk if it fails.',
      ],
    },
    {
      axis: 'N',
      dim: 'Will · Organisational alignment',
      short: 'Organisational alignment',
      text: 'How does the middle of the organisation behave once the strategy has been announced?',
      options: [
        'They translate it into their own team\'s goals and start reallocating people without being asked.',
        'They comply. The deck gets forwarded, the language gets adopted, the behaviour stays the same.',
        'The willing ones move; the rest are protected by their own operational KPIs.',
        'They wait it out. Most have seen three of these before and are expecting a fourth.',
      ],
    },
    {
      axis: 'N',
      dim: 'Will · Strategic focus',
      short: 'Strategic focus',
      text: 'What did we explicitly stop doing in order to make room for this transformation?',
      options: [
        'Nothing. Everything from last year is still on the list, and this is on top of it.',
        'We cut a few projects, though the largest legacy commitments remain untouchable.',
        'We “deprioritised” several things, but no budget and no headcount actually moved.',
        'We killed or divested named initiatives, said so publicly, and redeployed the people.',
      ],
    },
    {
      axis: 'K',
      dim: 'Skill · Talent & competency',
      short: 'Talent & competency',
      text: 'If the transformation\'s three most critical roles opened up tomorrow, could we fill them?',
      options: [
        'Only by hiring consultants, and the knowledge would walk out when they do.',
        'Yes: internally, from a bench we have deliberately built, within a quarter.',
        'No. We do not yet know with confidence what those three roles even are.',
        'We could hire externally, but onboarding and earning internal credibility would cost us a year.',
      ],
    },
    {
      axis: 'K',
      dim: 'Skill · Process & governance',
      short: 'Process & governance',
      text: 'How does a transformation decision that spans three departments actually get made?',
      options: [
        'Decision rights are written down. The accountable owner decides within days; the others are informed.',
        'It escalates until it reaches someone senior enough, or it quietly dies on the way up.',
        'Through relationships and goodwill. It works when the right people happen to get along.',
        'A steering committee handles it thoroughly, but on a monthly cadence.',
      ],
    },
    {
      axis: 'K',
      dim: 'Skill · Infrastructure & technology',
      short: 'Infrastructure & technology',
      text: 'What happens when a successful pilot needs to scale to the whole enterprise?',
      options: [
        'It doesn\'t. Our pilots live in a sandbox the core systems cannot talk to.',
        'It scales where our data is clean, which is roughly half the business.',
        'It scales on shared platforms and governed data, with rollout funding already committed.',
        'Technically possible, but every rollout becomes a bespoke integration project of its own.',
      ],
    },
    {
      axis: 'K',
      dim: 'Skill · Adaptive culture',
      short: 'Adaptive culture',
      text: 'When part of a transformation initiative fails, what happens next?',
      options: [
        'It gets reported honestly, and then absorbed into the plan without changing anything.',
        'It surfaces within days, is discussed openly, and the plan changes without drama.',
        'It is hidden until it is undeniable, and then someone is quietly reassigned.',
        'We learn, but slowly, and usually only at the quarterly review.',
      ],
    },
  ],
  archetypes: {
    PIPE: {
      name: 'The Pipe Dream',
      tag: '“All steering, no engine.”',
      sub: 'High Will · Low Skill',
      picture: 'A sleek, futuristic cockpit bolted onto a bicycle frame. Executive offsites generate brilliant multi-coloured strategy decks with five-year visions. The boardroom is energised, and on the ground nothing moves.',
      internal: 'Teams are trapped in perpetual fire-fighting. KPIs are missed quarter after quarter, producing initiative fatigue, cynicism and high turnover among the best people, who are exhausted from chasing moving targets without the tools or budget to hit them.',
      external: 'Markets and customers hear bold promises and announcements, then experience delivery delays, quality breakdowns and broken commitments. Credibility erodes faster than the strategy can be revised.',
      odds: 'Two paths. Either the capability gap is closed within 12–18 months and the ambition survives, or the third missed milestone converts the organisation\'s intent into cynicism, and you drop into the Drifter quadrant with a worse starting position than you had at the beginning.',
      impT: 'Build the engine',
      impP: 'Stop writing new strategy. Every hour spent on vision now has negative return. Redirect the entire effort to PMO governance, process engineering, platform and data investment, and targeted talent acquisition. Freeze the initiative portfolio at its current size until delivery throughput is demonstrably rising.',
      cases: [
        'GE',
        'DBS',
      ],
    },
    UNSTOP: {
      name: 'The Unstoppable',
      tag: '“Precision at full speed.”',
      sub: 'High Will · High Skill',
      picture: 'A high-performance Formula 1 car with an expert navigator. Every turn is calculated, the pit crew operates in millisecond synchronisation, and the machine accelerates cleanly toward each strategic objective.',
      internal: 'High trust, psychological safety and intense accountability coexist. Decisions are fast because teams understand both the strategic intent and the boundaries of their own authority. Results compound year over year rather than resetting each planning cycle.',
      external: 'Industry leader or category-defining innovator. Competitors react defensively to your moves rather than setting the agenda, and top-tier talent applies without being recruited.',
      odds: 'The real risk is no longer execution; it is complacency and horizon blindness. Organisations in this quadrant most often lose position not to a peer but to a business model they did not consider a competitor.',
      impT: 'Scale and protect',
      impP: 'Shift attention to Horizon 2 and 3 innovation, enterprise risk management, defensive moat-building and organisational resilience against black-swan disruption. Institutionalise what currently depends on specific individuals, so today\'s performance survives tomorrow\'s leadership change.',
      cases: [
        'DBS',
        'DEERE',
      ],
    },
    DRIFT: {
      name: 'The Drifter',
      tag: '“No map, no momentum.”',
      sub: 'Low Will · Low Skill',
      picture: 'A rowboat drifting on a fog-bound lake with neither oars nor compass. The crew sits idle, reacting only when water leaks through the hull or a sudden wave rocks the boat.',
      internal: 'Bureaucratic paralysis, apathy and low morale. People do the minimum required to maintain the status quo. Silos are deeply entrenched, and innovation is experienced as an unwanted disturbance rather than an opportunity.',
      external: 'Irrelevant or visibly declining. The market reads the company as a legacy remnant steadily losing share to more agile challengers, and increasingly prices it that way.',
      odds: 'This quadrant does not correct itself. Without external intervention, the typical trajectory is a slow margin decline punctuated by a discontinuity: a regulatory change, a technology shift, or an acquisition that removes the decision from your hands.',
      impT: 'Turnaround intervention',
      impP: 'This is not a transformation programme; it is a turnaround. Sequence: leadership realignment first, then strategic repositioning, then asset rationalisation, then core operational restructuring. Attempting capability building before the leadership question is settled reliably wastes both the budget and the remaining goodwill.',
      cases: [
        'FORD',
        'DEERE',
      ],
    },
    BLIND: {
      name: 'The Powerful Blind',
      tag: '“Full engine, bad compass.”',
      sub: 'Low Will · High Skill',
      picture: 'A freight train barrelling down the track at maximum speed, with nobody checking whether the bridge ahead is still standing.',
      internal: 'Hyper-efficient activity disconnected from value creation. Teams work overtime producing outputs that no longer match market demand or corporate objectives. Busywork is perfected; top-line growth stays flat.',
      external: 'Highly reliable at delivering yesterday\'s solutions. Customers respect the operational discipline, and migrate to competitors offering a forward-looking value proposition.',
      odds: 'The most dangerous quadrant, because the dashboards look green. Capability decays slowly and invisibly once it is pointed at the wrong target; by the time the P&L reveals the problem, the engine you were proud of has been optimised around a market that no longer exists.',
      impT: 'Redirect the energy',
      impP: 'You do not need more capability; you need a compass. Run a hard C-suite realignment on a refreshed vision, prune the portfolio explicitly, and rewire incentives from activity metrics (deployment velocity, projects delivered) to value metrics (EBITDA impact, cycle time, customer outcome). The engine you already have is worth more than any new programme.',
      cases: [
        'FORD',
        'DEERE',
      ],
    },
  },
  cases: {
    GE: {
      kind: 'fail',
      name: 'General Electric: Predix',
      p: 'GE launched GE Digital to build Predix, an industrial IoT operating system meant to turn a manufacturing giant into a software platform. Billions went into software development without a clear value proposition or a capability baseline across Aviation, Healthcare and Energy. Business units did not want the platform; GE Digital burned capital and was eventually spun off, contributing to the corporate breakup.',
      l: 'High intent, zero capability alignment. A software business model was pushed onto a hardware organisation without transforming the underlying capabilities first.',
    },
    FORD: {
      kind: 'fail',
      name: 'Ford: Smart Mobility',
      p: 'Ford created Ford Smart Mobility as a standalone unit for connected vehicles and ride-sharing, separate from its manufacturing lines. Isolating the transformation team in Silicon Valley created a cultural divide: the core business viewed the unit as an expensive distraction, while the unit could not influence core operations. The result was major write-downs, stock stagnation, a CEO departure, and eventual reintegration of digital back into the main business.',
      l: 'Transformation in a silo. Capability without aligned intent across the legacy operation produces motion, not change.',
    },
    DBS: {
      kind: 'win',
      name: 'DBS Bank: “World\'s Best Digital Bank”',
      p: 'In the late 2000s DBS Singapore was known for slow service and poor customer satisfaction. Under CEO Piyush Gupta the bank set out to behave like Google, Amazon, Netflix, Apple, LinkedIn and Facebook, and then, instead of buying off-the-shelf software, rewired its own architecture onto microservices and APIs while training more than 20,000 employees in agile and design thinking.',
      l: 'High intent matched with deep operational capability. Repeatedly named world\'s best bank, with a cost-to-income ratio and ROE well ahead of legacy peers.',
    },
    DEERE: {
      kind: 'win',
      name: 'John Deere: precision agriculture',
      p: 'A 180-year-old equipment manufacturer facing saturation in traditional tractor sales turned itself into a precision-agriculture technology platform: IoT sensors, computer vision and AI embedded in the machinery, surfaced through the Operations Center. Critically, Deere upskilled its dealer network to teach farmers data-driven farming and aligned dealer incentives with software adoption.',
      l: 'Transform the business model and the capability, not just the technology. Deere now charges SaaS subscriptions alongside hardware and holds a genuine software moat.',
    },
  },
}

/**
 * What each option is worth, by question index then option index.
 *
 * **Outside the dictionary on purpose, and this is the interesting decision in
 * this file.** `Translation` treats arrays as atomic, so a per-option `score`
 * inside `questions` would have forced every locale to restate all thirty-two
 * of them — which means a translator could silently change what the tool
 * measures by mistyping a number, and the compiler would be satisfied.
 *
 * Here the numbers exist once, in one language-free place, and
 * `Diagnostic.tsx` indexes into them positionally. A locale supplies text and
 * cannot supply scores. Reordering a locale's options would still break the
 * mapping — which is why the shuffled order is called out above — but no
 * translation can alter the arithmetic.
 */
export const DIAGNOSTIC_SCORES: number[][] = [
  [
    2,
    4,
    1,
    3,
  ],
  [
    3,
    1,
    4,
    2,
  ],
  [
    4,
    2,
    3,
    1,
  ],
  [
    1,
    3,
    2,
    4,
  ],
  [
    2,
    4,
    1,
    3,
  ],
  [
    4,
    1,
    2,
    3,
  ],
  [
    1,
    3,
    4,
    2,
  ],
  [
    2,
    4,
    1,
    3,
  ],
]
